import React from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Add, DeleteOutline, PictureAsPdf } from '@mui/icons-material';
import { EMPLOYMENT_TYPES, FORM_REFS } from '../../constants/nonCertifyingStaffAuthorization';
import GetsBrandHeader from '../certificationAuthorization/GetsBrandHeader';

const emptyScopeRow = () => ({
  scopeOfAuthorization: '',
  limitation: ''
});

export const emptyGetsCm001fCertificate = (overrides = {}) => ({
  dgcaApprovalNoDated: '',
  dgcaApprovalValidity: '',
  authorizationNo: 'GETS/OA/',
  oaRevisionNo: '00',
  oaRevisionDate: '',
  dateOfInitialOa: '',
  validFrom: '',
  validUntil: '',
  dateOfBirth: '',
  recurrentTrainingValidTill: '',
  scopeRows: [emptyScopeRow(), emptyScopeRow(), emptyScopeRow()],
  issued: true,
  ...overrides
});

const TERMS = [
  'The Holder will continue to be in employment/ contract with GETS-MRO.',
  'The Holder should be medically fit.',
  'The authorization number and date must be quoted while certifying the work.',
  'The authorization is valid for the period as specified and can be withdrawn at any time by the CMM, if deemed necessary.',
  'Previously issued authorization is here by invalidated.'
];

const sectionSx = {
  bgcolor: '#f0d9a8',
  border: '1px solid',
  borderColor: 'text.primary',
  px: 1.5,
  py: 0.75,
  fontWeight: 700,
  textAlign: 'center',
  textTransform: 'uppercase',
  fontSize: '0.85rem',
  letterSpacing: 0.3
};

function GetsCm001fCertificateForm({
  application,
  value,
  onChange,
  readOnly = false,
  onIssue,
  onDownloadPdf,
  saving = false
}) {
  const cert = value || emptyGetsCm001fCertificate();
  const scopeRows =
    Array.isArray(cert.scopeRows) && cert.scopeRows.length ? cert.scopeRows : [emptyScopeRow()];

  const set = (patch) => onChange({ ...cert, ...patch });
  const updateScope = (index, patch) => {
    const next = scopeRows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    set({ scopeRows: next });
  };

  const fieldProps = readOnly ? { InputProps: { readOnly: true } } : {};
  const dateVal = (v) => (v ? String(v).slice(0, 10) : '');

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2.5 }, borderColor: 'text.primary', borderWidth: 2, borderStyle: 'double' }}
    >
      <GetsBrandHeader
        title="Application for Issue / Extension / Renewal of Non-Certifying Staff Authorization"
        subtitle={`Form: ${FORM_REFS.certificate} | Issue: 01 Rev 00 Dtd Jul 2026`}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {onDownloadPdf && (
          <Button size="small" variant="outlined" startIcon={<PictureAsPdf />} onClick={onDownloadPdf}>
            Download {FORM_REFS.certificate} PDF
          </Button>
        )}
        {!readOnly && onIssue && (
          <Button size="small" variant="contained" disabled={saving} onClick={() => onIssue(true)}>
            Issue authorization
          </Button>
        )}
        {!readOnly && onIssue && (
          <Button size="small" variant="outlined" color="error" disabled={saving} onClick={() => onIssue(false)}>
            Do not issue
          </Button>
        )}
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            size="small"
            label="DGCA Approval No & Dated"
            value={cert.dgcaApprovalNoDated || ''}
            onChange={(e) => set({ dgcaApprovalNoDated: e.target.value })}
            {...fieldProps}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Validity"
            InputLabelProps={{ shrink: true }}
            value={dateVal(cert.dgcaApprovalValidity)}
            onChange={(e) => set({ dgcaApprovalValidity: e.target.value })}
            {...fieldProps}
          />
        </Grid>
      </Grid>

      <Grid container spacing={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
          <Box sx={{ ...sectionSx, border: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            Personal Details
          </Box>
          <Stack spacing={1.25} sx={{ p: 1.5 }}>
            <TextField fullWidth size="small" label="Name" value={application?.applicantName || ''} disabled />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date of Birth"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.dateOfBirth)}
              onChange={(e) => set({ dateOfBirth: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              required
              label="Authorisation No"
              value={cert.authorizationNo || ''}
              onChange={(e) => set({ authorizationNo: e.target.value })}
              {...fieldProps}
            />
            <TextField
              select={!readOnly}
              fullWidth
              size="small"
              label="Employment Type"
              value={application?.employeeType || ''}
              disabled
            >
              {EMPLOYMENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField fullWidth size="small" label="Employment No" value={application?.employeeId || ''} disabled />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ ...sectionSx, border: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
            Dates &amp; Validity
          </Box>
          <Stack spacing={1.25} sx={{ p: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date of Initial OA"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.dateOfInitialOa)}
              onChange={(e) => set({ dateOfInitialOa: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Recurrent Training Valid Till"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.recurrentTrainingValidTill)}
              onChange={(e) => set({ recurrentTrainingValidTill: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              label="CA Revision No."
              value={cert.oaRevisionNo || ''}
              onChange={(e) => set({ oaRevisionNo: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="CA Revision Date"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.oaRevisionDate)}
              onChange={(e) => set({ oaRevisionDate: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="CA Valid From"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.validFrom)}
              onChange={(e) => set({ validFrom: e.target.value })}
              {...fieldProps}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="CA Expire on"
              InputLabelProps={{ shrink: true }}
              value={dateVal(cert.validUntil)}
              onChange={(e) => set({ validUntil: e.target.value })}
              {...fieldProps}
            />
          </Stack>
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Details of Authorization
        </Typography>
        {!readOnly && (
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => set({ scopeRows: [...scopeRows, emptyScopeRow()] })}
          >
            Add row
          </Button>
        )}
      </Stack>
      <Table size="small" sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f0d9a8' }}>
            <TableCell width={48}>Sl. No</TableCell>
            <TableCell>Scope of Authorization</TableCell>
            <TableCell>Limitation</TableCell>
            {!readOnly && <TableCell width={40} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {scopeRows.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.scopeOfAuthorization || ''}
                  onChange={(e) => updateScope(index, { scopeOfAuthorization: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  variant="standard"
                  value={row.limitation || ''}
                  onChange={(e) => updateScope(index, { limitation: e.target.value })}
                  {...fieldProps}
                />
              </TableCell>
              {!readOnly && (
                <TableCell>
                  <IconButton
                    size="small"
                    disabled={scopeRows.length <= 1}
                    onClick={() => set({ scopeRows: scopeRows.filter((_, i) => i !== index) })}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ ...sectionSx, mb: 1 }}>Terms of Authorisation</Box>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        This authorization is issued under the authority of approved MOE.
      </Typography>
      <Box component="ol" sx={{ pl: 2.5, m: 0, typography: 'body2' }}>
        {TERMS.map((t) => (
          <li key={t.slice(0, 40)} style={{ marginBottom: 4 }}>
            {t}
          </li>
        ))}
      </Box>
      <Typography variant="body2" sx={{ mt: 1, mb: 2, fontStyle: 'italic', fontWeight: 600 }}>
        Note: It is the responsibility of the holder to ensure validity of this authorization during
        certification of work and capability/limitations of authorization also.
      </Typography>

      <Grid container spacing={1} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', p: 1.5 }}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700}>
            Compliance Monitoring Manager
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700} display="block" textAlign="center">
            SIGNATURE
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700} display="block" textAlign="center">
            SEAL
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={1} sx={{ border: '1px solid', borderColor: 'divider', p: 1.5 }}>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" fontWeight={700}>
            Holder&apos;s Signature
          </Typography>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Typography variant="caption" fontWeight={700} display="block" textAlign="center">
            SIGNATURE &amp; SEAL
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default GetsCm001fCertificateForm;
